import React, { useContext, useRef, useState } from 'react';

import { ControlledMenu, MenuItem, useHover } from '@szhsin/react-menu';
import { FaChevronDown } from 'react-icons/fa';
import { LocationContext } from '../../contexts/LocationContext';
import { NestedEntry, SingleEntry } from '../../graphql-fragments/navigationMenu';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { checkUrlsMatch, clsx } from '../../utils/utils';
import MenuEntry from './MenuEntry';
import * as styles from './NestedMenuEntry.module.scss';

export interface NestedMenuEntryProps {
  entry: NestedEntry;
  areasOfExpertiseEntries: Array<SingleEntry>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  headerWithOpaqueBackground?: boolean;
  isMobile?: boolean;
  className?: string;
}

const NestedMenuEntry = ({
  entry,
  areasOfExpertiseEntries,
  headerWithOpaqueBackground,
  isMobile,
  onClick,
  className,
}: NestedMenuEntryProps): React.ReactElement => {
  const location = useContext(LocationContext);

  const ref = useRef(null);
  const [isEntryOpen, setIsEntryOpen] = useState(false);
  const { anchorProps, hoverProps } = useHover(isEntryOpen, setIsEntryOpen);
  const [bodyRef, setBodyRef] = useState<HTMLDivElement | null>(null);

  const bodyMaxHeight = isEntryOpen && bodyRef ? bodyRef.scrollHeight : 0;

  const menuEntries = entry.entriesType === 'pages' ? entry.entries : areasOfExpertiseEntries;

  return (
    <div className={clsx(className, styles.container)}>
      {/* MOBILE */}
      <div className={styles.subMenuWrapperMobile}>
        <button
          onClick={() => {
            setIsEntryOpen(!isEntryOpen);
          }}
          className={clsx(
            styles.menuLink,
            styles.mobile,
            isEntryOpen && styles.nestedEntryOpen,
            location &&
              menuEntries.some(subEntry =>
                checkUrlsMatch(location.pathname, getUrlFromVersatileLink(subEntry)),
              ) &&
              styles.active,
            headerWithOpaqueBackground && styles.headerWithOpaqueBackground,
          )}
        >
          {entry.title}
        </button>
        <div
          className={styles.nestedEntries}
          ref={newRef => {
            setBodyRef(newRef);
          }}
          style={{ maxHeight: bodyMaxHeight }}
        >
          {menuEntries.map((subEntry, i) => (
            <MenuEntry
              entry={subEntry}
              withMorePaddingLeft
              key={i}
              onClick={onClick}
              headerWithOpaqueBackground={headerWithOpaqueBackground}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* DESKTOP */}
      <button
        className={clsx(
          styles.subMenuWrapperDesktop,
          isEntryOpen && styles.nestedEntryOpen,
          styles.menuLink,
          location &&
            menuEntries.some(subEntry =>
              checkUrlsMatch(location.pathname, getUrlFromVersatileLink(subEntry)),
            ) &&
            styles.active,
          headerWithOpaqueBackground && styles.headerWithOpaqueBackground,
        )}
        ref={ref}
        aria-expanded={isEntryOpen}
        aria-disabled="false"
        aria-controls={'panel_menu'}
        tabIndex={0}
        role="button"
        {...anchorProps}
      >
        {entry.title}
        <FaChevronDown className={styles.icon} />
      </button>

      <ControlledMenu
        {...hoverProps}
        menuClassName={clsx(
          styles.subMenuDesktop,
          headerWithOpaqueBackground && styles.headerWithOpaqueBackground,
        )}
        onClose={() => {
          setIsEntryOpen(false);
        }}
        state={isEntryOpen ? 'open' : 'closed'}
        anchorRef={ref}
        gap={22}
      >
        {menuEntries.map((subEntry, i) => (
          <MenuItem key={i} className={styles.nestedItem}>
            <MenuEntry
              entry={subEntry}
              key={i}
              onClick={onClick}
              headerWithOpaqueBackground={headerWithOpaqueBackground}
              isMobile={isMobile}
              isSubEntry
            />
          </MenuItem>
        ))}
      </ControlledMenu>
    </div>
  );
};

export default NestedMenuEntry;
